<template>
  <div>
    <!-- Toolbar -->
    <div class="d-flex justify-content-end mb-3">
      <b-button
        v-if="checkPermission('IP_BYPASS_INSERT')"
        variant="primary"
        size="sm"
        class="font-weight-bolder"
        @click="openCreate"
      >
        <i style="font-size: 1rem" class="flaticon2-add-1"></i> Thêm ngoại lệ
      </b-button>
    </div>

    <!-- Bộ lọc -->
    <b-row class="mb-4">
      <b-col cols="6" md="4">
        <b-form-input
          v-model="filter.searchQuery"
          size="sm"
          placeholder="Tìm tên nhân viên, cửa hàng, chức vụ..."
          @keyup.enter="fetchList(1)"
        />
      </b-col>
      <b-col cols="6" md="3">
        <b-form-select
          v-model="filter.subjectType"
          :options="subjectTypeOptions"
          size="sm"
          class="select-style"
        />
      </b-col>
      <b-col cols="6" md="3">
        <b-form-select
          v-model="filter.status"
          :options="statusOptions"
          size="sm"
          class="select-style"
        />
      </b-col>
      <b-col cols="auto">
        <b-button size="sm" variant="primary" style="width: 70px" @click="fetchList(1)">Lọc</b-button>
        <b-button size="sm" variant="secondary" class="ml-2" @click="resetFilter">Xóa lọc</b-button>
      </b-col>
    </b-row>

    <!-- Bảng danh sách -->
    <b-table
      :items="items"
      :fields="fields"
      :busy="isLoading"
      responsive
      hover
      bordered
      show-empty
      empty-text="Không có dữ liệu"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong> Đang tải...</strong>
        </div>
      </template>

      <template v-slot:cell(index)="row">
        {{ (currentPage - 1) * pageSize + row.index + 1 }}
      </template>

      <template v-slot:cell(subjectType)="row">
        <b-badge :variant="subjectTypeBadgeVariant(row.item.subjectType)">
          {{ subjectTypeLabel(row.item.subjectType) }}
        </b-badge>
      </template>

      <template v-slot:cell(expireAt)="row">
        <span v-if="!row.item.expireAt">Vô thời hạn</span>
        <span v-else :class="row.item.isExpiringSoon ? 'text-warning font-weight-bold' : ''">
          <i v-if="row.item.isExpiringSoon" class="fas fa-exclamation-triangle mr-1"></i>
          {{ formatDate(row.item.expireAt) }}
        </span>
      </template>

      <template v-slot:cell(status)="row">
        <b-badge :variant="row.item.status === 1 ? 'success' : 'secondary'">
          {{ row.item.status === 1 ? 'Kích hoạt' : 'Vô hiệu' }}
        </b-badge>
      </template>

      <template v-slot:cell(actions)="row">
        <div class="d-flex justify-content-center">
          <b-dropdown size="sm" no-caret right>
            <template slot="button-content">
              <i style="font-size: 1rem; padding-right: 0px" class="flaticon2-settings"></i>
            </template>
            <b-dropdown-item
              v-if="checkPermission('IP_BYPASS_UPDATE')"
              @click="openEdit(row.item)"
            >
              <span style="color: #3f4254; font-size: 12px">
                <i style="font-size: 1rem" class="flaticon2-pen"></i>&nbsp; Chỉnh sửa
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="checkPermission('IP_BYPASS_UPDATE') && row.item.status === 1"
              @click="patchStatus(row.item, 'lock')"
            >
              <span style="color: #3f4254; font-size: 12px">
                <i style="font-size: 1rem" class="fas fa-lock text-warning"></i>&nbsp; Khoá
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="checkPermission('IP_BYPASS_UPDATE') && row.item.status === 2"
              @click="patchStatus(row.item, 'unlock')"
            >
              <span style="color: #3f4254; font-size: 12px">
                <i style="font-size: 1rem" class="fas fa-lock-open text-success"></i>&nbsp; Mở khoá
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="checkPermission('IP_BYPASS_DELETE')"
              @click="confirmDelete(row.item)"
            >
              <span style="color: #d33; font-size: 12px">
                <i style="font-size: 1rem" class="flaticon2-rubbish-bin-delete-button"></i>&nbsp; Xóa
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <!-- Phân trang -->
    <b-col>
      <BPaginationNavCustom
        :number-of-pages="totalPages || 1"
        :current-page="currentPage"
        :total-page="totalPages"
        :linkParams="{}"
        @page-change="fetchList"
      />
    </b-col>

    <!-- Modal tạo/sửa -->
    <UpsertBypassModal
      :show="showModal"
      :item="selectedItem"
      :is-edit="isEdit"
      @close="closeModal"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import localData from '@/utils/saveDataToLocal';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import { IpBypassService } from './service/ip-access-control.service';
import UpsertBypassModal from './UpsertBypassModal.vue';
import { makeToastSuccess, handleError } from '@/utils/common';

export default {
  name: 'TabBypass',
  components: { BPaginationNavCustom, UpsertBypassModal },
  data() {
    return {
      items: [],
      totalPages: 0,
      currentPage: 1,
      pageSize: 10,
      isLoading: false,
      filter: { searchQuery: '', subjectType: null, status: null },
      showModal: false,
      isEdit: false,
      selectedItem: null,
      fields: [
        { key: 'index', label: 'STT', class: 'text-center', thStyle: { width: '60px' } },
        { key: 'subjectName', label: 'Đối tượng' },
        { key: 'subjectType', label: 'Loại', class: 'text-center' },
        { key: 'note', label: 'Ghi chú' },
        { key: 'expireAt', label: 'Hết hạn' },
        { key: 'createdByName', label: 'Người tạo' },
        { key: 'status', label: 'Trạng thái', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center', thStyle: { width: '60px' } },
      ],
      statusOptions: [
        { text: '-- Trạng thái --', value: null },
        { text: 'Kích hoạt', value: 1 },
        { text: 'Vô hiệu', value: 2 },
      ],
      subjectTypeOptions: [
        { text: '-- Loại --', value: null },
        { text: 'Nhân viên', value: 'employee' },
        { text: 'Chức vụ', value: 'jobTitle' },
        { text: 'Cửa hàng', value: 'store' },
      ],
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    checkPermission(key) {
      return localData.checkPermission(key);
    },
    fetchList(page = 1) {
      this.currentPage = page;
      this.isLoading = true;
      const params = {
        page: this.currentPage,
        size: this.pageSize,
        ...(this.filter.searchQuery && { searchQuery: this.filter.searchQuery }),
        ...(this.filter.subjectType && { subjectType: this.filter.subjectType }),
        ...(this.filter.status !== null && { status: this.filter.status }),
      };
      IpBypassService.getList(params)
        .then(({ data }) => {
          const result = data.data || {};
          this.items = result.data || [];
          this.totalPages = result.total_page || 0;
        })
        .catch(handleError)
        .finally(() => { this.isLoading = false; });
    },
    resetFilter() {
      this.filter = { searchQuery: '', subjectType: null, status: null };
      this.fetchList(1);
    },
    openCreate() {
      this.isEdit = false;
      this.selectedItem = null;
      this.showModal = true;
    },
    openEdit(item) {
      this.isEdit = true;
      this.selectedItem = { ...item };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedItem = null;
    },
    onSaved() {
      this.closeModal();
      this.fetchList(this.currentPage);
    },
    patchStatus(item, action) {
      IpBypassService.patchStatus(item.id, action)
        .then(() => {
          const msg = action === 'lock' ? 'Đã khoá ngoại lệ' : 'Đã mở khoá ngoại lệ';
          makeToastSuccess(msg);
          this.fetchList(this.currentPage);
        })
        .catch(handleError);
    },
    confirmDelete(item) {
      this.$swal.fire({
        title: 'Xóa ngoại lệ bypass',
        text: `Bạn có chắc muốn xóa ngoại lệ này không?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
      }).then(result => {
        if (result.isConfirmed) {
          IpBypassService.patchStatus(item.id, 'delete')
            .then(() => {
              makeToastSuccess('Đã xóa ngoại lệ');
              this.fetchList(this.currentPage);
            })
            .catch(handleError);
        }
      });
    },
    subjectTypeLabel(type) {
      const map = { employee: 'Nhân viên', jobTitle: 'Chức vụ', store: 'Cửa hàng' };
      return map[type] || type;
    },
    subjectTypeBadgeVariant(type) {
      const map = { employee: 'primary', jobTitle: 'info', store: 'warning' };
      return map[type] || 'secondary';
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('vi-VN');
    },
  },
};
</script>
